import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  AddFormComponent,
  FormField,
  ActionButton,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { SKUService } from '@app/feature/center-business/goods/sku/services/sku.service';
import { SKUDetail } from '@app/feature/center-business/goods/sku/models/sku.interface';
import { GoodsSkuControllerService } from '@app/api/api/goodsSkuController.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { forkJoin, of, from, Subject } from 'rxjs';
import { map, catchError, switchMap, takeUntil } from 'rxjs/operators';
import { formatCurrencyVND } from '@shared/utils/date-formatter.util';
import { ChannelIntegrationService } from '@app/api/channel-service/api/channelIntegration.service';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { OnlineShopControllerService } from '@app/api/channel-service/api/onlineShopController.service';
import { OnlineSkuDetailVO } from '@app/api/channel-service/model/onlineSkuDetailVO';
import { LazadaCategoryAttributeDTO } from '@app/api/channel-service/model/lazadaCategoryAttributeDTO';
import { SpuApiService } from '@app/feature/center-business/goods/spu/services/spu-api.service';
import { parseLazadaQuantityFromProductRecord } from '../../../utils/lazada-integration.util';
import {
  extractLazadaCategoryAttributesFromBody,
  mapLazadaCategoryListResponseToOptions,
  mapLazadaCategorySuggestionResponseToOptions,
  partitionLazadaAttributesByMandatory,
} from '../../../utils/lazada-category-api.util';
import { MaterialService } from '@app/feature/center-business/goods/material/services/material.service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import {
  isLazadaChannelCode,
  resolveOnlineStoreChannelCode,
} from '../../../utils/lazada-integration.util';
import { isShopeeChannelCode } from '../../../utils/shopee-integration.util';
import { PriceListService } from '@app/feature/center-business/goods/price-list/services/price-list.service';

@Component({
  selector: 'app-product-listing-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
    TranslateModule,
    AddFormComponent,
  ],
  providers: [MessageService],
  templateUrl: './product-listing-form.component.html',
  styleUrls: ['./product-listing-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListingFormComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly skuService = inject(SKUService);
  private readonly goodsSkuController = inject(GoodsSkuControllerService);
  private readonly channelIntegrationService = inject(ChannelIntegrationService);
  private readonly spuApiService = inject(SpuApiService);
  private readonly materialService = inject(MaterialService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly httpClient = inject(HttpClient);
  private readonly onlineShopService = inject(OnlineShopControllerService);
  private readonly priceListService = inject(PriceListService);
  private readonly destroy$ = new Subject<void>();
  private isProductDataLoaded = false;

  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  // Form data
  formData = signal<any>({});
  selectedSkuId = signal<string>('');
  selectedSkuDetail = signal<SKUDetail | null>(null);
  skuOptions = signal<Array<{ label: string; value: string }>>([]);
  availableStockSkuCodes = signal<Set<string>>(new Set());
  categoryOptions = signal<Array<{ label: string; value: string }>>([]);
  categoryAttributes = signal<Array<LazadaCategoryAttributeDTO>>([]);
  siblingSkus = signal<any[]>([]);
  selectedVariationsTable = signal<any[]>([]);
  loading = signal(false);
  private skipCanDeactivate = false;
  private isUpdatingFromSku = false;
  private isLoadingImages = false; // Flag to prevent formFields re-render while images are being loaded
  private productAttributesFromApi = signal<{ [key: string]: string } | null>(null);
  private pendingApiCalls = signal<number>(0); // Track pending API calls
  availableStock = signal<number | null>(null);
  private currentLazadaQuantity = 0;
  editMode = signal(false);
  viewMode = signal(false);
  productId?: string;
  onlineStoreId?: string;
  channelSubTypeCode = signal('');

  isShopeeChannel = computed(() => isShopeeChannelCode(this.channelSubTypeCode()));
  isLazadaChannel = computed(() => isLazadaChannelCode(this.channelSubTypeCode()));

  hasImagesFromSkuOrSpu = computed<boolean>(() => {
    const currentData = this.formData();
    
    const hasValidImageUrl = (img: any): boolean => {
      if (!img || img === null) return false;
      if (Array.isArray(img)) return false;
      
      if (typeof img === 'object') {
        const url = img.previewUrl || img.url;
        return url && typeof url === 'string' && url.trim().length > 0;
      }
      
      if (typeof img === 'string') {
        return img.trim().length > 0;
      }
      
      return false;
    };
    
    const hasMainImage = currentData.mainImage && 
                        currentData.mainImage !== null &&
                        currentData.mainImage !== undefined &&
                        !Array.isArray(currentData.mainImage) &&
                        typeof currentData.mainImage === 'object' &&
                        hasValidImageUrl(currentData.mainImage);
    
    const hasDetailImages = Array.isArray(currentData.detailImages) && 
                           currentData.detailImages.length > 0 &&
                           currentData.detailImages.some(hasValidImageUrl);
    
    return hasMainImage || hasDetailImages;
  });

  private initAvailableStockFromNavigation(): void {
    try {
      const state = (this.router.getCurrentNavigation()?.extras?.state as any) ?? (window.history.state as any);
      const raw = state?.availableStock;
      const value =
        typeof raw === 'number'
          ? raw
          : raw !== undefined && raw !== null
            ? Number(raw)
            : NaN;
      if (!Number.isNaN(value)) {
        this.availableStock.set(value);
      }
    } catch {
      // Ignore navigation state errors
    }
  }

  formFields = computed<FormField[]>(() => {
    const fields: FormField[] = [
      {
        label: 'onlineStore.form.product_listing.fields.product_code',
        controlName: 'skuCode',
        type: 'dropdown',
        section: 'onlineStore.form.product_listing.sections.sku_info',
        placeholder: 'onlineStore.form.product_listing.placeholders.select_spu',
        options: this.skuOptions(),
        required: true,
        filter: true,
        showClear: !this.viewMode() && !this.editMode(), // Disable clear in view/edit mode
        readonly: this.viewMode() || this.editMode(), // Readonly in view/edit mode
      },
    ];

    const skuDetail = this.selectedSkuDetail();
    const isViewOrEdit = this.viewMode() || this.editMode();
    const hasVariations = this.siblingSkus().length > 1;

    if (skuDetail || isViewOrEdit) {
      const currentCategoryOptions = [...this.categoryOptions()];
      fields.push(
        {
          label: 'onlineStore.form.product_listing.fields.marketplace_product_name',
          controlName: 'marketplaceProductName',
          type: 'text',
          section: 'onlineStore.form.product_listing.sections.marketplace_info',
          placeholder: 'onlineStore.form.product_listing.placeholders.enter_product_name',
          required: true,
        },
        {
          label: 'onlineStore.form.product_listing.fields.marketplace_category',
          controlName: 'marketplaceCategory',
          type: 'dropdown',
          section: 'onlineStore.form.product_listing.sections.marketplace_info',
          placeholder: 'onlineStore.form.product_listing.placeholders.enter_category',
          options: currentCategoryOptions,
          required: true,
          showClear: true,
        }
      );

      const hasVariations = this.siblingSkus().length > 1;
      if (!hasVariations) {
        // Lazada: readonly cost + retail from price-service (Shopee uses variation table columns).
        if (this.isLazadaChannel()) {
          fields.push(
            {
              label: 'onlineStore.form.product_listing.fields.cost_price',
              controlName: 'costPrice',
              type: 'currency',
              section: 'onlineStore.form.product_listing.sections.marketplace_info',
              readonly: true,
              disabled: true,
            },
            {
              label: 'onlineStore.form.product_listing.fields.selling_price',
              controlName: 'sellingPrice',
              type: 'currency',
              section: 'onlineStore.form.product_listing.sections.marketplace_info',
              readonly: true,
              disabled: true,
            },
          );
        }
        fields.push({
          label: 'onlineStore.form.product_listing.fields.marketplace_price',
          controlName: 'marketplacePrice',
          type: 'currency',
          section: 'onlineStore.form.product_listing.sections.marketplace_info',
          placeholder: 'onlineStore.form.product_listing.placeholders.enter_price',
          required: true,
        });
      }

      // Show "Số lượng bổ sung" only in edit mode (when updating an existing product listing) and no variations
      if (this.editMode() && !hasVariations) {
        fields.push({
          label: 'onlineStore.form.product_listing.fields.additional_quantity',
          controlName: 'additionalQuantity',
          type: 'number',
          section: 'onlineStore.form.product_listing.sections.marketplace_info',
          placeholder: 'onlineStore.form.product_listing.placeholders.enter_additional_quantity',
          min: 0,
          max: this.availableStock() ?? undefined,
          errorMessages: {
            max: 'onlineStore.form.product_listing.messages.additional_quantity_exceeds_stock',
          },
        });
      }
    }

    const attributes = [...this.categoryAttributes()];
    if (attributes.length > 0) {
      const { required: requiredAttributes, optional: optionalAttributes } =
        partitionLazadaAttributesByMandatory(attributes);
      const sortedAttributes = [...requiredAttributes, ...optionalAttributes];
      
      sortedAttributes.forEach((attr: LazadaCategoryAttributeDTO) => {
        const controlName = `categoryAttribute_${attr.id}`;
        const isRequired = attr.isMandatory === 1;
        const isKeyProp = attr.isKeyProp === 1;

          const optionMap = new Map<string, string>(); // value -> enName
          const options =
            attr.options && attr.options.length > 0
              ? attr.options.map((opt) => {
                  const value = opt.id?.toString() || opt.name || '';
                  const enName = opt.enName || opt.name || '';
                  optionMap.set(value, enName);
                  return {
                    label: opt.name || opt.enName || '',
                    value: value,
                  };
                })
              : undefined;

          let field: FormField;

          switch (attr.inputType) {
            case 'singleSelect':
              // Same type as search box (searchableDropdown) for consistent UX in category attributes
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'searchableDropdown',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.enter_or_select_required'
                  : 'common.form.placeholders.enter_or_select',
                options: options || [],
                required: isRequired,
                showClear: !isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                  allowFreeText: false,
                },
              };
              break;

            case 'multiSelect':
              // Multi-select dropdown with options (values separated by comma)
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'dropdown',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.select_required'
                  : 'common.form.placeholders.select',
                options: options || [],
                required: isRequired,
                multiple: true,
                showClear: !isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                  separator: ',',
                },
              };
              break;

            case 'enumInput':
              // Searchable dropdown (can select from options or enter free text)
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'searchableDropdown',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.enter_or_select_required'
                  : 'common.form.placeholders.enter_or_select',
                options: options || [],
                required: isRequired,
                showClear: !isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                  allowFreeText: true,
                },
              };
              break;

            case 'multiEnumInput':
              // Multi-select searchable dropdown (can select from options or enter free text, values separated by comma)
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'searchableDropdown',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.enter_or_select_required'
                  : 'common.form.placeholders.enter_or_select',
                options: options || [],
                required: isRequired,
                multiple: true,
                showClear: !isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                  allowFreeText: true,
                  separator: ',',
                },
              };
              break;

            case 'numeric':
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'number',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.enter_required'
                  : 'common.form.placeholders.enter',
                required: isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                },
              };
              break;

            case 'text':
            default:
              field = {
                label: attr.label || attr.name || '',
                controlName: controlName,
                type: 'text',
                section: 'onlineStore.form.product_listing.sections.category_attributes',
                placeholder: isRequired
                  ? 'common.form.placeholders.enter_required'
                  : 'common.form.placeholders.enter',
                required: isRequired,
                disabled: this.viewMode(),
                readonly: this.viewMode(),
                customData: {
                  isKeyProp: isKeyProp,
                  attributeId: attr.id,
                  attributeName: attr.name || '',
                  optionMap: optionMap,
                },
              };
              break;
          }

          fields.push(field);
      });
    }

    if (skuDetail || isViewOrEdit) {
      // Use file / multi-file for product images (same as edit product listing).
      // user can delete and change images. Do not switch to galleria-display.
      fields.push({
        label: 'onlineStore.form.product_listing.fields.main_image',
        controlName: 'mainImage',
        type: 'file',
        placeholder: 'onlineStore.form.product_listing.placeholders.upload_main_image',
        section: 'onlineStore.form.product_listing.sections.product_images',
        accept: '.jpg,.jpeg,.png,.webp,.bmp,.svg',
        multiple: true,
        maxFiles: 1,
        maxSize: 2 * 1024 * 1024,
        acceptedTypes: 'onlineStore.form.product_listing.placeholders.accepted_main_image_types',
        required: true,
        errorMessages: {
          required: 'material.form.validation.main_image_required',
        },
      });
      fields.push({
        label: 'onlineStore.form.product_listing.fields.detail_images',
        controlName: 'detailImages',
        type: 'multi-file',
        placeholder: 'onlineStore.form.product_listing.placeholders.upload_detail_images',
        section: 'onlineStore.form.product_listing.sections.product_images',
        accept: '.jpg,.jpeg,.png,.webp,.bmp,.svg',
        multiple: true,
        maxFiles: 7,
        maxSize: 2 * 1024 * 1024,
        acceptedTypes: 'common.form.placeholders.accepted_material_detail_image_types',
      });
    }

    // Section: Product Description
    // Always show in view/edit mode, or when SKU detail is available
    if (skuDetail || isViewOrEdit) {
      fields.push({
        label: 'onlineStore.form.product_listing.fields.product_description',
        controlName: 'productDescription',
        type: 'html-description',
        placeholder: 'material.form.placeholders.enter_product_asset_description',
        section: 'onlineStore.form.product_listing.sections.product_description',
        maxLength: 2000,
        colspan: 2,
        customStyles: {
          height: '150px',
          minHeight: '150px',
          maxHeight: '500px',
        },
      });
    }

    return fields;
  });

  config = computed(() => {
    const fields = this.formFields();
    return {
      title: this.editMode()
        ? 'onlineStore.form.product_listing.titles.edit'
        : this.viewMode()
        ? 'onlineStore.form.product_listing.titles.view'
        : 'onlineStore.form.product_listing.titles.add',
      formFields: fields,
      headerActions: this.viewMode()
        ? ([
            {
              label: 'common.actions.back',
              action: 'cancel',
              severity: 'secondary',
              outlined: true,
            },
          ] as ActionButton[])
        : ([
            {
              label: 'common.actions.cancel',
              action: 'cancel',
              severity: 'secondary',
              outlined: true,
            },
            {
              label: 'onlineStore.form.product_listing.actions.save_draft',
              action: 'save_draft',
              severity: 'secondary',
              outlined: true,
            },
            {
              label: 'onlineStore.form.product_listing.actions.save_and_publish',
              action: 'save_and_publish',
              severity: 'info',
            },
          ] as ActionButton[]),
    listRoute: this.getListRoute(),
    };
  });

  ngOnInit(): void {
    this.initAvailableStockFromNavigation();
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const newOnlineStoreId = params['id'];
        const newProductId = params['productId'];
        
        if (newProductId !== this.productId || newOnlineStoreId !== this.onlineStoreId) {
          this.isProductDataLoaded = false;
          this.skuOptions.set([]);
          
          this.onlineStoreId = newOnlineStoreId;
          this.productId = newProductId;
          this.loadOnlineStoreChannelType();
          
          if (this.productId) {
            const isView = this.route.snapshot.url.some(
              (segment) => segment.path === 'view'
            );
            this.viewMode.set(isView);
            this.editMode.set(!isView);
            this.loadProductData();
          } else if (this.onlineStoreId) {
            this.viewMode.set(false);
            this.editMode.set(false);
            this.loadSkuOptions();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveChannelPlatformFromShopDetail(detail: any): string {
    return (
      resolveOnlineStoreChannelCode({
        channelSubTypeCode:
          detail?.channelSubTypeCode ?? detail?.onlineStoreTypeCode,
        channelType: detail?.channelType,
        channelPlatform: detail?.channelPlatform,
      }) || ''
    );
  }

  private loadOnlineStoreChannelType(): void {
    if (!this.onlineStoreId) {
      this.channelSubTypeCode.set('');
      return;
    }

    const shopId = parseInt(this.onlineStoreId, 10);
    if (isNaN(shopId)) {
      this.channelSubTypeCode.set('');
      return;
    }

    this.onlineShopService
      .showDetails1(shopId)
      .pipe(
        switchMap((response: any) => {
          if (response instanceof Blob) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response ?? {});
        }),
        catchError((error) => {
          console.error('Error loading online store channel type:', error);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((detail) => {
        if (detail) {
          this.channelSubTypeCode.set(
            this.resolveChannelPlatformFromShopDetail(detail),
          );
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Load SKU options for dropdown from /v1/channel-integration/stock?shopId={shop ID}
   */
  private loadSkuOptions(): void {
    if (!this.onlineStoreId) {
      console.error('Online store ID is missing, cannot load SKU options');
      this.skuOptions.set([]);
      return;
    }

    this.loading.set(true);
    
    // Get base path from service configuration
    const basePath = this.onlineShopService.configuration.basePath || '';
    const shopId = parseInt(this.onlineStoreId, 10);
    
    if (isNaN(shopId)) {
      console.error('Invalid shopId:', this.onlineStoreId);
      this.skuOptions.set([]);
      this.loading.set(false);
      return;
    }
    
    const url = `${basePath}/v1/channel-integration/stock`;
    
    // Build query parameters
    let queryParams = new HttpParams();
    queryParams = queryParams.set('shopId', shopId.toString());
    
    // Build headers
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    
    this.httpClient
      .get<any>(url, {
        headers: headers,
        params: queryParams,
        observe: 'response',
      })
      .pipe(
        switchMap((response: HttpResponse<any>) => {
          // Handle Blob response
          if (response.body instanceof Blob) {
            return from(BlobConverterUtil.convertToJson(response.body));
          }
          return of(response.body || {});
        }),
        map((response: any) => {
          const items = Array.isArray(response) 
            ? response 
            : response?.pageContent || response?.content || response?.data || [];
          
          const codes = new Set<string>();
          items.forEach((item: any) => {
            if (item.skuCode) {
              codes.add(item.skuCode);
            }
          });
          this.availableStockSkuCodes.set(codes);

          const uniqueSpus = new Map<string, any>();
          items.forEach((item: any) => {
            const key = item.spuCode || item.skuCode || '';
            if (key && !uniqueSpus.has(key)) {
              uniqueSpus.set(key, item);
            }
          });

          return Array.from(uniqueSpus.values())
            .map((item: any) => {
              const spuCode = item.spuCode || item.skuCode || '';
              const spuName = item.spuName || item.skuName || '';
              const label = spuName ? `${spuCode} - ${spuName}` : spuCode;
              return {
                label: label,
                value: item.skuId?.toString() || item.id?.toString() || item.skuCode || '',
              };
            })
            .filter((option: { label: string; value: string }) => 
              option.value && option.value.trim() !== '' && option.label && option.label.trim() !== ''
            );
        }),
        catchError((error) => {
          console.error('Error loading SKU options:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (options) => {
          this.skuOptions.set(options);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.skuOptions.set([]);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load product data for edit/view mode from API /v1/channel-integration/product/{id}
   */
  private loadProductData(): void {
    if (!this.productId) {
      console.error('Product ID is missing');
      return;
    }

    // Prevent loading multiple times
    if (this.isProductDataLoaded) {
      return;
    }

    this.isProductDataLoaded = true;
    this.loading.set(true);
    // Track product detail API (first API call)
    this.pendingApiCalls.set(1);
    const productIdNum = parseInt(this.productId, 10);
    
    if (isNaN(productIdNum)) {
      console.error('Invalid product ID:', this.productId);
      this.loading.set(false);
      this.pendingApiCalls.set(0);
      return;
    }

    this.channelIntegrationService
      .getOnlineSkuDetail(productIdNum, 'response')
      .pipe(
        switchMap((response: HttpResponse<any>) => {
          // Handle Blob response
          if (response.body instanceof Blob) {
            return from(BlobConverterUtil.convertToJson(response.body));
          }
          return of(response.body || {});
        }),
        catchError((error) => {
          console.error('Error loading product data:', error);
          // Decrement pending API calls even on error
          this.pendingApiCalls.update(count => count - 1);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('onlineStore.messages.load_product_detail_error'),
          });
          return of(null);
        })
      )
      .subscribe({
        next: (productData: any) => {
          // Decrement pending API calls for product detail API
          this.pendingApiCalls.update(count => count - 1);
          
          if (!productData) {
            this.isProductDataLoaded = false; // Reset flag on error
            this.checkAndFinishLoading();
            return;
          }

          this.currentLazadaQuantity =
            parseLazadaQuantityFromProductRecord(productData);

          if (productData.attributes && typeof productData.attributes === 'object') {
            this.productAttributesFromApi.set(productData.attributes);
          } else {
            this.productAttributesFromApi.set(null);
          }

          // Support both camelCase and snake_case from API
          const productSkuCode = (productData.skuCode ?? productData.sku_code ?? '').toString().trim();
          const productSkuId = productData.skuId ?? productData.sku_id;

          if (productSkuCode) {
            const currentOptions = this.skuOptions();
            const optionExists = currentOptions.some(
              (opt) => opt.value === productSkuCode && opt.label === productSkuCode
            );
            if (!optionExists) {
              const skuOption: { label: string; value: string } = {
                label: productSkuCode,
                value: productSkuCode,
              };
              this.skuOptions.set([skuOption]);
            }
          }

          // API may return mainImage/detailImages or images array; support snake_case
          const mainImageUrl =
            productData.mainImage ??
            (productData as any).main_image ??
            productData.images?.[0];
          const detailImageUrls =
            Array.isArray(productData.detailImages) && productData.detailImages.length > 0
              ? productData.detailImages
              : Array.isArray((productData as any).detail_images) && (productData as any).detail_images.length > 0
                ? (productData as any).detail_images
                : productData.images && productData.images.length > 1
                  ? productData.images.slice(1)
                  : [];

          const mappedData: any = {
            skuCode: productSkuCode,
            siblings: productData.siblings || [],
            marketplaceProductName:
              (productData.name ?? productData.marketplaceProductName ?? (productData as any).marketplace_product_name ?? '').toString().trim(),
            marketplaceCategory:
              (productData.categoryId ?? (productData as any).category_id ?? productData.marketplaceCategory ?? (productData as any).marketplace_category ?? '').toString().trim(),
            marketplacePrice:
              Number(productData.price ?? productData.marketplacePrice ?? (productData as any).marketplace_price ?? 0) || 0,
            productDescription:
              (productData.description ?? productData.productDescription ?? (productData as any).product_description ?? '').toString().trim(),
            mainImage: mainImageUrl
              ? {
                  previewUrl: mainImageUrl,
                  fileName: this.extractFileNameFromUrl(mainImageUrl) || 'image.jpg',
                  fileSize: 0,
                  isExisting: true,
                }
              : undefined,
            detailImages: detailImageUrls.map((url: string) => ({
              previewUrl: url,
              fileName: this.extractFileNameFromUrl(url) || 'image.jpg',
              fileSize: 0,
              isExisting: true,
            })),
            ...(productData.attributes && this.mapCategoryAttributesFromMap(productData.attributes)),
          };

          this.formData.set(mappedData);
          
          // Patch form controls after form is ready
          setTimeout(() => {
            if (!this.addFormComponent?.form) {
              return;
            }
            
            // Batch all form control updates to reduce re-renders
            const updates: Array<{ control: any; value: any }> = [];
            
            // Collect all updates first
            Object.keys(mappedData).forEach((key) => {
              const control = this.addFormComponent?.form.get(key);
              if (control) {
                const value = mappedData[key];
                if (key === 'mainImage') {
                  let mainImageValue: any = null;
                  if (Array.isArray(value)) {
                    mainImageValue = value.length > 0 ? value[0] : null;
                  } else if (value !== null && value !== undefined) {
                    mainImageValue = value;
                  }
                  updates.push({ control, value: mainImageValue });
                } else if (key === 'detailImages') {
                  const arrayValue = Array.isArray(value) ? value : (value ? [value] : []);
                  updates.push({ control, value: arrayValue });
                } else if (value !== null && value !== undefined && value !== '') {
                  updates.push({ control, value });
                }
              }
            });
            
            if (productSkuCode) {
              const skuCodeControl = this.addFormComponent.form.get('skuCode');
              if (skuCodeControl) {
                // In edit/view mode always set SKU from API; in add mode require option to exist
                const isEditOrView = this.viewMode() || this.editMode();
                const formFields = this.formFields();
                const skuField = formFields.find((f) => f.controlName === 'skuCode');
                const hasOption = skuField?.options?.length && skuField.options.some((opt) => opt.value === productSkuCode);
                if (isEditOrView || hasOption) {
                  updates.push({ control: skuCodeControl, value: productSkuCode });
                  if (isEditOrView) {
                    skuCodeControl.disable({ emitEvent: false });
                  }
                }
              }
            }
            
            updates.forEach(({ control, value }) => {
              control.setValue(value, { emitEvent: false });
            });

            // Sync images with the addFormComponent signals
            if (this.addFormComponent) {
              const mainImageVal = this.addFormComponent.form.get('mainImage')?.value;
              const detailImagesVal = this.addFormComponent.form.get('detailImages')?.value;
              this.addFormComponent.updateMainImagesFromMaterial(mainImageVal || null);
              this.addFormComponent.updateDetailedImagesFromMaterial(detailImagesVal || []);
            }
            
            // Single change detection after all updates
            this.cdr.markForCheck();
          }, 300);
          
          const categoryId = mappedData.marketplaceCategory;
          const categoryNameFromApi = productData.categoryName ?? (productData as any).category_name ?? '';
          if (categoryNameFromApi) {
            mappedData.productCategoryName = categoryNameFromApi;
          }
          
          // If SKU ID is available, load SKU detail first, then load category suggestions/attributes
          // This ensures we have categoryName from SKU detail before calling suggestion API
          if (productSkuId !== null && productSkuId !== undefined) {
            const skuIdNum = Number(productSkuId);
            if (!isNaN(skuIdNum) && skuIdNum > 0) {
              this.selectedSkuId.set(productSkuCode || skuIdNum.toString());
              // Increment pending API calls for SKU detail
              this.pendingApiCalls.update(count => count + 1);
              
              // Load SKU detail, and in the callback, load category suggestions/attributes
              // This ensures categoryName from SKU detail is available before calling suggestion API
              this.loadSkuDetail(skuIdNum.toString(), () => {
                const detail = this.selectedSkuDetail();
                if (detail) {
                  const spuCode = detail.spuCode || '';
                  const spuName = detail.spuName || '';
                  const label = spuName ? `${spuCode} - ${spuName}` : spuCode;
                  this.skuOptions.set([{ label: label, value: productSkuCode }]);
                }
                
                // After SKU detail is loaded, load category suggestions and attributes
                // This ensures we have categoryName from SKU detail
                if (categoryId && this.onlineStoreId) {
                  this.loadCategorySuggestionsAndAttributes(categoryId);
                }
              });
            } else {
              console.warn('Invalid productSkuId:', productSkuId, '- skipping SKU detail load');
              // No valid SKU ID, load category suggestions/attributes directly
              if (categoryId && this.onlineStoreId) {
                this.loadCategorySuggestionsAndAttributes(categoryId);
              }
            }
          } else {
            // No SKU ID, load category suggestions/attributes directly
            if (categoryId && this.onlineStoreId) {
              this.loadCategorySuggestionsAndAttributes(categoryId);
            } else {
              // No category, check if all pending APIs are done
              // If no pending APIs (no SKU detail to load), finish loading after form controls are set
              // Otherwise, wait for SKU detail to complete
              if (this.pendingApiCalls() === 0) {
                // No pending APIs, finish loading after form controls are set
                setTimeout(() => {
                  this.loading.set(false);
                  this.cdr.markForCheck();
                }, 400); // Wait a bit longer than form control setting (300ms)
              }
              // If there are pending APIs (e.g., SKU detail), they will call checkAndFinishLoading()
            }
          }
        },
        error: (error) => {
          console.error('Error in product data subscription:', error);
          this.isProductDataLoaded = false; // Reset flag on error
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Map category attributes from API response attributes map to form data
   */
  private mapCategoryAttributesFromMap(attributes: { [key: string]: string }): any {
    const formData: any = {};
    if (attributes && typeof attributes === 'object') {
      // Attributes map has format: { "categoryAttribute_123": "value", ... }
      Object.keys(attributes).forEach((key) => {
        if (key.startsWith('categoryAttribute_')) {
          formData[key] = attributes[key];
        }
      });
    }
    return formData;
  }

  /**
   * Load SKU detail when SKU is selected (list new product).
   * Calls goods/v1/goods/sku/{id}?isEdit=false, then goods/v1/goods/material/{materialId}
   * for mainImageUrls and detailImageUrls so product images display like edit product listing.
   */
  private loadSkuDetail(skuId: string, onComplete?: () => void): void {
    if (!skuId) {
      this.selectedSkuDetail.set(null);
      // Decrement pending API calls if this was tracked
      this.checkAndFinishLoading();
      return;
    }

    // Validate that skuId is a valid number
    // getSkuDetailById uses parseInt internally, so we need to ensure it's numeric
    const skuIdNum = parseInt(skuId, 10);
    if (isNaN(skuIdNum) || skuIdNum <= 0) {
      console.error('Invalid SKU ID for loadSkuDetail:', skuId, '- must be a positive number');
      this.selectedSkuDetail.set(null);
      // Decrement pending API calls if this was tracked
      this.checkAndFinishLoading();
      return;
    }

    // Use the validated numeric string
    this.skuService.getSkuDetailById(skuIdNum.toString()).subscribe({
      next: (detail) => {
        this.selectedSkuDetail.set(detail);

        console.log('loadSkuDetail detail response:', detail);
        const spuIdNum = detail.spuId ? Number(detail.spuId) : null;
        if (spuIdNum && !isNaN(spuIdNum) && this.isShopeeChannel()) {
          console.log('Querying goodsSkuDetail with spuId:', spuIdNum);
          this.goodsSkuController.goodsSkuDetail(spuIdNum).subscribe({
            next: (listResult) => {
              console.log('goodsSkuDetail query result:', listResult);
              if (listResult) {
                const availableCodes = this.availableStockSkuCodes();
                let filteredList = listResult.filter((item: any) => 
                  item.skuCode && (availableCodes.size === 0 || availableCodes.has(item.skuCode))
                );

                const siblingList: any[] = this.formData().siblings || [];
                const priceByCode = new Map<string, any>(
                  siblingList.map((s: any) => [s.skuCode, s.price])
                );
                const qtyByCode = new Map<string, any>(
                  siblingList.map((s: any) => [s.skuCode, s.quantity])
                );

                if (this.editMode() || this.viewMode()) {
                  const publishedCodes = new Set<string>(
                    siblingList.map((s: any) => s.skuCode).filter(Boolean)
                  );
                  filteredList = filteredList.filter((item: any) =>
                    item.skuCode && (publishedCodes.size === 0 || publishedCodes.has(item.skuCode))
                  );
                }

                const mappedResult = filteredList.map((item: any) => {
                  const actualPrice = priceByCode.get(item.skuCode);
                  const actualQty = qtyByCode.get(item.skuCode);
                  return {
                    ...item,
                    originalSalesPrice: item.salesPrice,
                    salesPrice:
                      actualPrice !== undefined && actualPrice !== null
                        ? actualPrice
                        : item.salesPrice,
                    addedQuantity: this.viewMode()
                      ? actualQty !== undefined && actualQty !== null
                        ? actualQty
                        : 0
                      : 0,
                  };
                });
                this.enrichSiblingPricesFromPriceService(mappedResult);
              } else {
                this.siblingSkus.set([]);
                this.selectedVariationsTable.set([]);
              }
              this.cdr.markForCheck();
            },
            error: (err) => {
              console.error('Error querying goodsSkuDetail:', err);
              this.siblingSkus.set([]);
              this.selectedVariationsTable.set([]);
              this.cdr.markForCheck();
            }
          });
        } else {
          console.warn('Selected SKU detail has no valid spuId:', detail);
          this.siblingSkus.set([]);
        }

        const isAddMode = !this.editMode() && !this.viewMode();
        if (isAddMode) {
          this.isUpdatingFromSku = true;
          // Clear category state so UI and form rebuild with new SKU; new values come from updateFormDataFromSku
          this.categoryOptions.set([]);
          this.categoryAttributes.set([]);
          this.productAttributesFromApi.set(null);
        }

        this.updateFormDataFromSku(detail);

        if (detail.materialId || detail.spuId) {
          this.isLoadingImages = true;
        }
        
        const materialId = detail.materialId != null ? (typeof detail.materialId === 'number' ? detail.materialId : Number(detail.materialId) || detail.materialId) : null;
        if (materialId != null && materialId !== '') {
          this.loadMaterialImages(materialId as string | number, () => {
            this.isLoadingImages = false;
            this.cdr.markForCheck();
          });
        } else if (detail.spuId) {
          this.loadSpuImages(detail.spuId, () => {
            this.isLoadingImages = false;
            this.cdr.markForCheck();
          });
        }
        
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 200);

        this.checkAndFinishLoading();
      },
      error: (error) => {
        console.error('Error loading SKU detail:', error);
        this.selectedSkuDetail.set(null);
        this.isUpdatingFromSku = false;

        // Execute callback even on error to ensure category suggestions/attributes are still loaded
        if (onComplete) {
          setTimeout(() => {
            onComplete();
          }, 200);
        }
        
        // Decrement pending API calls even on error
        this.checkAndFinishLoading();
      },
    });
  }

  /**
   * Product name for Lazada category suggestion API (productName query param).
   * Uses SKU name after selecting from channel-integration/stock.
   */
  private getCategorySuggestionProductName(): string {
    const skuDetail = this.selectedSkuDetail();
    const formData = this.formData();
    return (skuDetail?.skuName || formData.skuName || '').trim();
  }

  /**
   * Parse API response (including Blob) to JSON.
   */
  private parseCategoryResponse(response: any): Promise<any> {
    if (response instanceof Blob) {
      return new Promise<any>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            resolve(JSON.parse(reader.result as string));
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsText(response);
      });
    }
    return Promise.resolve(response);
  }

  /**
   * Load category suggestions from Lazada API; when suggestion returns empty, use category list API.
   * Calls /v1/channel-integration/category/suggestion and /v1/channel-integration/category/list.
   * @param productName Product name (SKU name) to search for suggestions
   * @param onComplete Optional callback to execute after options are loaded
   */
  private loadCategorySuggestions(productName: string, onComplete?: () => void): void {
    if (!this.onlineStoreId) {
      return;
    }

    const shopId = Number(this.onlineStoreId);
    if (isNaN(shopId)) {
      console.error('Invalid shopId:', this.onlineStoreId);
      return;
    }

    const suggestion$ = (productName
      ? this.channelIntegrationService.getCategorySuggestion(shopId, productName)
      : of([])
    ).pipe(
      switchMap((response: any) =>
        productName ? from(this.parseCategoryResponse(response)) : of([]),
      ),
      map((response: any) =>
        mapLazadaCategorySuggestionResponseToOptions(response),
      ),
      catchError((error) => {
        console.error('Error loading category suggestions:', error);
        return of([]);
      }),
    );

    const list$ = this.channelIntegrationService.getCategoryList(shopId).pipe(
      switchMap((response: any) => from(this.parseCategoryResponse(response))),
      map((response: any) => mapLazadaCategoryListResponseToOptions(response)),
      catchError((error) => {
        console.error('Error loading category list:', error);
        return of([]);
      }),
    );

    forkJoin({ suggestion: suggestion$, list: list$ })
      .pipe(
        map(({ suggestion, list }) =>
          suggestion.length > 0 ? suggestion : list,
        ),
      )
      .subscribe({
        next: (options) => {
          requestAnimationFrame(() => {
            this.categoryOptions.set(options);
            this.cdr.markForCheck();
          });
          if (onComplete) {
            onComplete();
          }
          this.checkAndFinishLoading();
        },
        error: (error) => {
          console.error('Error loading category options:', error);
          requestAnimationFrame(() => {
            this.categoryOptions.set([]);
            this.cdr.markForCheck();
          });
          if (onComplete) {
            onComplete();
          }
          this.checkAndFinishLoading();
        },
      });
  }

  /**
   * Load category attributes from Lazada API
   */
  private loadCategoryAttributes(categoryId: string): void {
    if (!this.onlineStoreId || !categoryId) {
      return;
    }

    const shopId = Number(this.onlineStoreId);
    if (isNaN(shopId)) {
      console.error('Invalid shopId:', this.onlineStoreId);
      return;
    }

    this.channelIntegrationService
      .getCategoryAttributes(shopId, categoryId)
      .pipe(
        switchMap((response: any) => {
          // Handle Blob response
          if (response instanceof Blob) {
            return new Promise<any>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const jsonData = JSON.parse(reader.result as string);
                  resolve(jsonData);
                } catch (error) {
                  reject(error);
                }
              };
              reader.onerror = reject;
              reader.readAsText(response);
            });
          }
          return of(response);
        }),
        map((response: any) => {
          const body = response?.body != null ? response.body : response;
          return extractLazadaCategoryAttributesFromBody(
            body,
          ) as LazadaCategoryAttributeDTO[];
        }),
        catchError((error) => {
          console.error('Error loading category attributes:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (attributes) => {
          this.categoryAttributes.set(attributes);
          
          // Wait for formFields to update and form controls to be set, then map attributes
          // Form controls are set in setTimeout(300ms) in loadProductData()
          // Wait longer to ensure form controls are ready before mapping attributes
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Map category attributes from product detail API to form controls
                // This should be called after category attributes are loaded, formFields are updated, and form controls are set
                if (this.productAttributesFromApi()) {
                  this.mapCategoryAttributesToFormData();
                }
                
                // Decrement pending API calls and check if all APIs are done
                this.checkAndFinishLoading();
              });
            });
          }, 400); // Wait for form controls to be set (300ms) + buffer
        },
        error: () => {
          this.categoryAttributes.set([]);
          // Wait for form controls to be set even on error
          setTimeout(() => {
            requestAnimationFrame(() => {
              // Decrement pending API calls even on error
              this.checkAndFinishLoading();
            });
          }, 400);
        },
      });
  }

  /**
   * Load category suggestions and attributes sequentially, then map attributes
   * This ensures all data is loaded and mapped before displaying
   * IMPORTANT: API suggestion must be called BEFORE API attribute
   */
  private loadCategorySuggestionsAndAttributes(categoryId: string): void {
    if (!this.onlineStoreId || !categoryId) {
      // If no category, check if all pending APIs are done
      this.checkAndFinishLoading();
      return;
    }

    const formData = this.formData();
    const suggestionProductName = this.getCategorySuggestionProductName();

    // ALWAYS try to call suggestion API first with SKU name as productName
    if (suggestionProductName) {
      this.pendingApiCalls.update(count => count + 1);

      this.loadCategorySuggestions(suggestionProductName, () => {
        this.pendingApiCalls.update(count => count + 1);
        this.loadCategoryAttributes(categoryId);
      });
    } else {
      // No SKU name available, load attributes directly
      console.warn('No SKU name available for suggestion API, loading attributes directly. categoryId:', categoryId);
      this.pendingApiCalls.update(count => count + 1);
      this.loadCategoryAttributes(categoryId);
    }
  }

  /**
   * Decrement pending API calls counter and finish loading if all APIs are done
   * Also ensures form controls are set and attributes are mapped before finishing loading
   */
  private checkAndFinishLoading(): void {
    this.pendingApiCalls.update(count => {
      const newCount = count - 1;
      if (newCount <= 0) {
        // All APIs completed, wait for form controls to be set and attributes to be mapped
        // Form controls are set in setTimeout(300ms) in loadProductData()
        // Attributes mapping happens in loadCategoryAttributes subscribe
        // Wait a bit longer to ensure everything is ready before displaying UI
        setTimeout(() => {
          requestAnimationFrame(() => {
            this.loading.set(false);
            this.cdr.markForCheck();
          });
        }, 500); // Wait longer than form control setting (300ms) + buffer for attribute mapping
      }
      return newCount;
    });
  }

  /**
   * Get attribute value from API map by attribute name (flexible key match: exact, then case-insensitive).
   */
  private getAttributeValueFromApi(
    attributesFromApi: { [key: string]: string },
    attrName: string,
  ): string | undefined {
    if (!attrName || !attributesFromApi || typeof attributesFromApi !== 'object') {
      return undefined;
    }
    if (attributesFromApi[attrName] !== undefined && attributesFromApi[attrName] !== null && attributesFromApi[attrName] !== '') {
      return attributesFromApi[attrName];
    }
    const lower = attrName.toLowerCase();
    const key = Object.keys(attributesFromApi).find((k) => k.toLowerCase() === lower);
    return key != null ? attributesFromApi[key] : undefined;
  }

  /**
   * Map category attributes from API response attributes map to form data.
   * API format: { [attributeName: string]: string } e.g. { language: "Vietnamese" }.
   * Multi-select controls must receive an array; single-select a scalar.
   */
  private mapCategoryAttributesToFormData(): void {
    const attributesFromApi = this.productAttributesFromApi();
    if (!attributesFromApi || !this.addFormComponent?.form) {
      return;
    }

    const categoryAttrs = this.categoryAttributes();
    if (categoryAttrs.length === 0) {
      return;
    }

    const isMultiValueType = (inputType: string | undefined) =>
      inputType === 'multiSelect' || inputType === 'multiEnumInput';

    categoryAttrs.forEach((attr: LazadaCategoryAttributeDTO) => {
      const controlName = `categoryAttribute_${attr.id}`;
      const control = this.addFormComponent?.form.get(controlName);
      if (!control) {
        return;
      }

      const attributeValue = this.getAttributeValueFromApi(attributesFromApi, attr.name || '');
      if (attributeValue === undefined || attributeValue === null || attributeValue === '') {
        return;
      }

      const valueStr = String(attributeValue).trim();
      const multiValue = isMultiValueType(attr.inputType);

      if (attr.options && attr.options.length > 0) {
        const parts = multiValue ? valueStr.split(',').map((s) => s.trim()).filter(Boolean) : [valueStr];
        const optionValues: string[] = [];
        for (const part of parts) {
          const matchingOption = attr.options.find(
            (opt) => opt.enName === part || opt.name === part || String(opt.id) === part,
          );
          if (matchingOption) {
            const ov = matchingOption.id?.toString() || matchingOption.name || '';
            if (ov) optionValues.push(ov);
          } else {
            optionValues.push(part);
          }
        }
        if (optionValues.length > 0) {
          const valueToSet = multiValue ? optionValues : optionValues[0];
          control.setValue(valueToSet, { emitEvent: false });
        }
      } else {
        if (multiValue) {
          const arr = valueStr.includes(',') ? valueStr.split(',').map((s) => s.trim()).filter(Boolean) : [valueStr];
          control.setValue(arr, { emitEvent: false });
        } else {
          control.setValue(valueStr, { emitEvent: false });
        }
      }
    });

    this.cdr.markForCheck();
  }

  /**
   * Update form data from SKU detail. In add mode: marketplace, category attributes, and images
   * are always taken from the new SKU so the form reflects the selected SKU.
   */
  private updateFormDataFromSku(detail: SKUDetail): void {
    const currentData = this.formData();
    const isAddMode = !this.editMode() && !this.viewMode();
    if (isAddMode) {
      this.isUpdatingFromSku = true;
    }

    // Load images from SKU detail: try all possible API field names (materialMainImages, imageList, mainImage, images, etc.)
    const pickValid = (v: any): string[] => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.filter((u: any) => u != null && u !== '' && typeof u === 'string');
      if (typeof v === 'string' && v !== '') return [v];
      return [];
    };
    const d: any = detail;
    const candidatesMain = [
      d.materialMainImages,
      d.material_main_images,
      d.mainImage,
      d.main_image,
      d.imageList,
      d.image_list,
    ];
    const candidatesDetail = [
      d.imageList,
      d.image_list,
      d.detailImage,
      d.detail_image,
      d.images,
      d.materialMainImages,
      d.material_main_images,
    ];
    let imageUrls: string[] = [];
    for (const c of candidatesMain) {
      const arr = pickValid(c);
      if (arr.length > 0) {
        imageUrls = arr;
        break;
      }
    }
    if (imageUrls.length === 0) {
      for (const c of candidatesDetail) {
        const arr = pickValid(c);
        if (arr.length > 0) {
          imageUrls = arr;
          break;
        }
      }
    }
    
    // Map images to form data format
    // First image = main image (single object), rest = detail images (array)
    // Always clear images first when loading new SKU (images will be set below if available)
    let mainImageData: any = undefined;
    let detailImagesData: any[] = [];
    
    // Only load images if not in edit/view mode (to avoid overwriting existing images)
    if (!this.editMode() && !this.viewMode() && imageUrls.length > 0) {
      // Main image: first image from list
      if (imageUrls[0]) {
        mainImageData = {
          previewUrl: imageUrls[0],
          fileName: this.extractFileNameFromUrl(imageUrls[0]) || 'image.jpg',
          fileSize: 0,
          isExisting: true,
        };
      }
      
      // Detail images: remaining images (from index 1 onwards)
      if (imageUrls.length > 1) {
        detailImagesData = imageUrls.slice(1).map((url: string) => ({
          previewUrl: url,
          fileName: this.extractFileNameFromUrl(url) || 'image.jpg',
          fileSize: 0,
          isExisting: true,
        }));
      }
    }
    
    // In add mode: take marketplace and images from new SKU only; strip category attribute keys
    const base = isAddMode
      ? (() => {
          const o = { ...currentData };
          Object.keys(o).forEach((k) => {
            if (k.startsWith('categoryAttribute_')) delete (o as Record<string, unknown>)[k];
          });
          return o;
        })()
      : { ...currentData };

    const newData = {
      ...base,
      skuName: detail.skuName || '',
      skuCodeDisplay: detail.skuCode || '',
      productName: detail.spuName || '',
      productCategory: detail.categoryName || '',
      productCode: detail.spuCode || '',
      productBrand: detail.brandName || '',
      costPrice: detail.costPrice || 0,
      sellingPrice: detail.salesPrice || 0,
      promotionPrice: detail.activityPrice || 0,
      marketplaceProductName:
        this.editMode() || this.viewMode()
          ? currentData.marketplaceProductName
          : (detail.spuName ?? ''),
      marketplaceCategory:
        this.editMode() || this.viewMode()
          ? currentData.marketplaceCategory
          : (detail.categoryName ?? ''),
      marketplacePrice:
        this.editMode() || this.viewMode()
          ? currentData.marketplacePrice
          : (detail.salesPrice ?? 0),
      mainImage: mainImageData ?? undefined,
      detailImages: detailImagesData.length > 0 ? detailImagesData : [],
    };
    this.formData.set(newData);
    this.applyCatalogPricesFromPriceService(detail.skuCode || '', {
      preserveMarketplacePrice: this.editMode() || this.viewMode(),
    });
    
    // Trigger change detection to ensure AddFormComponent receives updated initialData
    this.cdr.markForCheck();

    const applyImageControls = (): void => {
      if (!this.addFormComponent?.form || this.editMode() || this.viewMode()) return;
      const mainImageControl = this.addFormComponent.form.get('mainImage');
      const detailImagesControl = this.addFormComponent.form.get('detailImages');
      if (mainImageData && mainImageControl && !mainImageControl.disabled) {
        mainImageControl.setValue(mainImageData, { emitEvent: false });
        this.addFormComponent.updateMainImagesFromMaterial(mainImageData);
      } else if (mainImageControl && !mainImageControl.disabled && !(detail.materialId || detail.spuId)) {
        mainImageControl.setValue(null, { emitEvent: false });
        this.addFormComponent.updateMainImagesFromMaterial(null);
      }
      if (detailImagesData.length > 0 && detailImagesControl && !detailImagesControl.disabled) {
        detailImagesControl.setValue(detailImagesData, { emitEvent: false });
        this.addFormComponent.updateDetailedImagesFromMaterial(detailImagesData);
      } else if (detailImagesControl && !detailImagesControl.disabled) {
        detailImagesControl.setValue([], { emitEvent: false });
        this.addFormComponent.updateDetailedImagesFromMaterial([]);
      }
      this.addFormComponent.markForCheck();
      this.cdr.markForCheck();
    };

    const applyNonImageFields = (data: Record<string, unknown>): void => {
      if (!this.addFormComponent?.form || this.editMode() || this.viewMode()) return;
      Object.keys(data).forEach((key) => {
        if (key === 'mainImage' || key === 'detailImages') return;
        const control = this.addFormComponent?.form.get(key);
        if (control && !control.disabled && data[key] !== undefined) {
          control.setValue(data[key], { emitEvent: false });
        }
      });
    };

    setTimeout(() => {
      applyImageControls();
      applyNonImageFields(newData);
      this.cdr.markForCheck();
      setTimeout(() => {
        applyImageControls();
        applyNonImageFields(newData);
        this.cdr.markForCheck();
        if (isAddMode) {
          this.isUpdatingFromSku = false;
        }
      }, 350);
    }, 50);
  }

  /**
   * Load product images from material (mainImageUrls, detailImageUrls).
   * @param materialId Material ID
   * @param onComplete Callback when done
   */
  private loadMaterialImages(materialId: string | number, onComplete?: () => void): void {
    if (!materialId) {
      return;
    }

    const materialIdNum = typeof materialId === 'string' ? parseInt(materialId, 10) : materialId;
    if (isNaN(materialIdNum) || materialIdNum <= 0) {
      return;
    }

    // Only load images if not in edit/view mode (to avoid overwriting existing images)
    if (this.editMode() || this.viewMode()) {
      return;
    }

    this.materialService.getMaterialById(materialIdNum.toString()).subscribe({
      next: (material: any) => {
        if (!material) {
          return;
        }

        const rawMainImageUrls: string[] = Array.isArray(material.mainImageUrls)
          ? material.mainImageUrls.filter((u: any) => u != null && u !== '' && typeof u === 'string')
          : (Array.isArray(material.mainImage) ? material.mainImage.filter((u: any) => u != null && u !== '' && typeof u === 'string') : []);
        const rawDetailImageUrls: string[] = Array.isArray(material.detailImageUrls)
          ? material.detailImageUrls.filter((u: any) => u != null && u !== '' && typeof u === 'string')
          : (Array.isArray(material.detailImage) ? material.detailImage.filter((u: any) => u != null && u !== '' && typeof u === 'string') : []);

        const mainImageUrls = rawMainImageUrls;
        const detailImageUrls = rawDetailImageUrls;
        let mainImageData: any = undefined;
        let detailImagesData: any[] = [];

        if (mainImageUrls.length > 0) {
          mainImageData = {
            previewUrl: mainImageUrls[0],
            fileName: this.extractFileNameFromUrl(mainImageUrls[0]) || 'image.jpg',
            fileSize: 0,
            isExisting: true,
          };
        }
        if (detailImageUrls.length > 0) {
          detailImagesData = detailImageUrls.map((url: string) => ({
            previewUrl: url,
            fileName: this.extractFileNameFromUrl(url) || 'image.jpg',
            fileSize: 0,
            isExisting: true,
          }));
        }

        const currentData = this.formData();
        const newData = {
          ...currentData,
          mainImage: mainImageData ?? undefined,
          detailImages: detailImagesData.length > 0 ? detailImagesData : [],
        };
        this.formData.set(newData);
        this.cdr.markForCheck();

        const applyMaterialImages = (): void => {
          if (!this.addFormComponent?.form) return;
          if (mainImageData) {
            const c = this.addFormComponent.form.get('mainImage');
            if (c && !c.disabled) {
              c.setValue(mainImageData, { emitEvent: true });
              this.addFormComponent.updateMainImagesFromMaterial(mainImageData);
            }
          }
          if (detailImagesData.length > 0) {
            const c = this.addFormComponent.form.get('detailImages');
            if (c && !c.disabled) {
              c.setValue(detailImagesData, { emitEvent: true });
              this.addFormComponent.updateDetailedImagesFromMaterial(detailImagesData);
            }
          }
          this.addFormComponent.markForCheck();
          this.cdr.markForCheck();
        };
        setTimeout(() => {
          applyMaterialImages();
          if (onComplete) onComplete();
          setTimeout(applyMaterialImages, 200);
        }, 300);

        // If no images from material, fallback to SPU images
        if (!mainImageData && detailImagesData.length === 0) {
          // Clear images if material has no images
          const currentData = this.formData();
          const clearedData = {
            ...currentData,
            mainImage: undefined,
            detailImages: [],
          };
          this.formData.set(clearedData);
          setTimeout(() => {
            if (this.addFormComponent?.form) {
              const mainImageControl = this.addFormComponent.form.get('mainImage');
              const detailImagesControl = this.addFormComponent.form.get('detailImages');
              if (mainImageControl && !mainImageControl.disabled) {
                mainImageControl.setValue(null, { emitEvent: false });
              }
              if (detailImagesControl && !detailImagesControl.disabled) {
                detailImagesControl.setValue([], { emitEvent: false });
              }
              if (this.addFormComponent) {
                this.addFormComponent.updateMainImagesFromMaterial(null);
                this.addFormComponent.updateDetailedImagesFromMaterial([]);
              }
              this.cdr.markForCheck();
            }
          }, 200);
          const skuDetail = this.selectedSkuDetail();
          if (skuDetail?.spuId) {
            this.loadSpuImages(skuDetail.spuId, () => {
              this.isLoadingImages = false;
              this.cdr.markForCheck();
              if (onComplete) {
                onComplete();
              }
            });
          } else {
            // No SPU, reset flag
            this.isLoadingImages = false;
            this.cdr.markForCheck();
            if (onComplete) {
              onComplete();
            }
          }
        }
      },
      error: (error) => {
        console.error('Error loading material images:', error);
        // Fallback to SPU images if material load fails
        const skuDetail = this.selectedSkuDetail();
        if (skuDetail?.spuId) {
          this.loadSpuImages(skuDetail.spuId, () => {
            this.isLoadingImages = false;
            this.cdr.markForCheck();
          });
        } else {
          // No SPU, reset flag
          this.isLoadingImages = false;
          this.cdr.markForCheck();
        }
        
        // Execute callback even on error
        if (onComplete) {
          onComplete();
        }
      },
    });
  }

  /**
   * Load SPU images (mainImageUrls and detailImageUrls) from SPU detail
   * Similar to SPU ordinary view - load images from SPU when SPU ID is available
   * This has lower priority than material images
   * @param spuId SPU ID to load images from
   * @param onComplete Optional callback to execute after images are loaded
   */
  private loadSpuImages(spuId: string | number, onComplete?: () => void): void {
    if (!spuId) {
      return;
    }

    const spuIdNum = typeof spuId === 'string' ? parseInt(spuId, 10) : spuId;
    if (isNaN(spuIdNum) || spuIdNum <= 0) {
      return;
    }

    // Only load images if not in edit/view mode (to avoid overwriting existing images)
    if (this.editMode() || this.viewMode()) {
      return;
    }

    this.spuApiService.getSpuById(spuIdNum).subscribe({
      next: (spuDetail: any) => {
        if (!spuDetail) {
          return;
        }

        // Get images from SPU detail (mainImageUrls and detailImageUrls)
        // Similar to how SPU form wrapper loads images from material
        // Filter out null, undefined, and empty string values
        const rawMainImageUrls: string[] = spuDetail.mainImageUrls || [];
        const rawDetailImageUrls: string[] = spuDetail.detailImageUrls || [];
        
        const mainImageUrls: string[] = rawMainImageUrls.filter((url: any) => 
          url !== null && url !== undefined && url !== '' && typeof url === 'string'
        );
        const detailImageUrls: string[] = rawDetailImageUrls.filter((url: any) => 
          url !== null && url !== undefined && url !== '' && typeof url === 'string'
        );

        // Map images to form data format
        // First image from mainImageUrls = main image (single object)
        // All detailImageUrls = detail images (array)
        let mainImageData: any = undefined;
        let detailImagesData: any[] = [];

        // Main image: first image from mainImageUrls
        if (mainImageUrls.length > 0) {
          mainImageData = {
            previewUrl: mainImageUrls[0],
            fileName: this.extractFileNameFromUrl(mainImageUrls[0]) || 'image.jpg',
            fileSize: 0,
            isExisting: true,
          };
        }

        // Detail images: all images from detailImageUrls
        if (detailImageUrls.length > 0) {
          detailImagesData = detailImageUrls.map((url: string) => ({
            previewUrl: url,
            fileName: this.extractFileNameFromUrl(url) || 'image.jpg',
            fileSize: 0,
            isExisting: true,
          }));
        }

        // Always update images from SPU (even if empty) to clear old images
        // SPU images have priority over SKU images
        const currentData = this.formData();
        const newData = {
          ...currentData,
          // SPU images have priority - always use SPU images if available
          // If no SPU images, explicitly clear old images (set to undefined/empty array)
          // This ensures hasImagesFromSkuOrSpu() returns false when no images
          mainImage: mainImageData ? mainImageData : undefined,
          detailImages: detailImagesData.length > 0 ? detailImagesData : [],
        };
        this.formData.set(newData);
        
        // Trigger change detection to ensure AddFormComponent receives updated initialData
        this.cdr.markForCheck();

        // Update form controls and AddFormComponent signals if form is ready
        // Similar to how SPU form wrapper updates images from material
        setTimeout(() => {
          if (this.addFormComponent?.form) {
            // Always update main image control (even if null/undefined to clear old images)
            const mainImageControl = this.addFormComponent.form.get('mainImage');
            if (mainImageControl && !mainImageControl.disabled) {
              mainImageControl.setValue(mainImageData || null, { emitEvent: false });
            }
            // Update AddFormComponent's main images signal to display the image or clear it
            this.addFormComponent.updateMainImagesFromMaterial(mainImageData || null);
            
            // Always update detail images control (even if empty array to clear old images)
            const detailImagesControl = this.addFormComponent.form.get('detailImages');
            if (detailImagesControl && !detailImagesControl.disabled) {
              detailImagesControl.setValue(detailImagesData, { emitEvent: false });
            }
            // Update AddFormComponent's detailed images signal to display the images or clear them
            this.addFormComponent.updateDetailedImagesFromMaterial(detailImagesData);
            
            // Trigger change detection to refresh getExistingFiles()
            this.cdr.markForCheck();
          }
          
          // Execute callback after images are loaded
          if (onComplete) {
            onComplete();
          }
        }, 200);
        
      },
      error: (error) => {
        console.error('Error loading SPU images:', error);
        // Don't show error to user - images from SKU detail are already loaded
        
        // Execute callback even on error
        if (onComplete) {
          onComplete();
        }
      },
    });
  }

  /**
   * Handle form value changes. When SKU changes we only update skuCode and load new SKU;
   * marketplace, category attributes, and images are set from API in updateFormDataFromSku.
   */
  onFormValueChanges(formValue: any): void {
    const previousData = this.formData();

    if (formValue.skuCode && formValue.skuCode !== this.selectedSkuId()) {
      const skuIdNum = parseInt(formValue.skuCode, 10);
      if (!isNaN(skuIdNum) && skuIdNum > 0) {
        this.selectedSkuId.set(formValue.skuCode);
        this.formData.set({ ...previousData, skuCode: formValue.skuCode });
        this.loadSkuDetail(skuIdNum.toString(), () => {
          const productName = this.getCategorySuggestionProductName();
          if (productName && this.onlineStoreId) {
            this.loadCategorySuggestions(productName);
          }
        });
        return;
      }
      this.selectedSkuId.set(formValue.skuCode);
      this.selectedSkuDetail.set(null);
    } else if (!formValue.skuCode) {
      this.selectedSkuId.set('');
      this.selectedSkuDetail.set(null);
      this.categoryOptions.set([]);
      this.categoryAttributes.set([]);
    }

    if (!this.isUpdatingFromSku) {
      this.formData.set(formValue);
    }

    // If marketplace category changed, call API attribute (not suggestion)
    // Load category attributes when marketplace category changes
    // Only load if not updating from SKU (to prevent loading when autofilling from SKU)
    // Also skip if SKU was just selected (suggestion was already called)
    if (
      !this.isUpdatingFromSku &&
      formValue.marketplaceCategory &&
      formValue.marketplaceCategory !== previousData.marketplaceCategory &&
      formValue.skuCode === previousData.skuCode // Only load if SKU didn't change (to avoid duplicate calls)
    ) {
      // Only call attribute API when category is selected
      // Suggestion API should have been called when SKU was selected
      if (formValue.marketplaceCategory && this.onlineStoreId) {
        this.loadCategoryAttributes(formValue.marketplaceCategory);
      }
    } else if (!formValue.marketplaceCategory) {
      // Clear attributes when category is cleared
      this.categoryAttributes.set([]);
    }
  }

  /**
   * Handle form actions (cancel, save_draft, save_and_publish)
   */
  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'cancel':
        this.onCancel();
        break;
      case 'save_draft':
        this.onSaveDraft();
        break;
      case 'save_and_publish':
        this.onSaveAndPublish();
        break;
    }
  }

  /**
   * Handle cancel action
   */
  private onCancel(): void {
    this.router.navigate([this.getListRoute()]);
  }

  canDeactivate(): boolean {
    if (this.viewMode() || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  /**
   * Handle save draft action
   */
  private onSaveDraft(): void {
    this.saveProduct(true);
  }

  /**
   * Handle save and publish action
   */
  private onSaveAndPublish(): void {
    this.saveProduct(false);
  }

  /**
   * Save product (draft or publish) by calling API /v1/channel-integration/product/upload
   */
  private saveProduct(isSaveDraft: boolean): void {
    const form = this.addFormComponent?.form;

    if (!form) {
      return;
    }

    const formValue = form.getRawValue();
    const available = this.availableStock();
    const rawAdditional = formValue.additionalQuantity;
    const additionalQuantity =
      rawAdditional !== undefined && rawAdditional !== null && rawAdditional !== ''
        ? Number(rawAdditional)
        : 0;

    // Show specific error when additional quantity exceeds available stock
    if (available !== null && !Number.isNaN(additionalQuantity) && additionalQuantity > available) {
      const additionalControl = form.get('additionalQuantity');
      const existingErrors = additionalControl?.errors || {};
      additionalControl?.setErrors({ ...existingErrors, max: true });
      additionalControl?.markAsTouched();

      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'onlineStore.form.product_listing.messages.additional_quantity_exceeds_stock',
        ),
      });
      return;
    }

    if (!form.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach((key) => {
        form.get(key)?.markAsTouched();
      });

      // Name the missing fields and jump to the first one. The required category-attribute
      // fields are easy to overlook, so a generic "validation error" left users staring at
      // a form that looks complete.
      const invalidNames = Object.keys(form.controls).filter(
        (key) => form.get(key)?.invalid,
      );
      const fields = this.formFields();
      const labelFor = (name: string): string => {
        const field = fields.find((f) => f.controlName === name);
        const attrName = (field?.customData as { attributeName?: string } | undefined)
          ?.attributeName;
        if (attrName) return attrName;
        return field?.label ? this.translateService.instant(field.label) : name;
      };
      const missingLabels = invalidNames.map(labelFor);

      const first = invalidNames[0];
      if (first) {
        setTimeout(() => {
          const el = document.querySelector(
            `[formcontrolname="${first}"], #${first}`,
          ) as HTMLElement | null;
          const target = (el?.closest('.field, .p-field, .form-field') as HTMLElement) || el;
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el?.focus?.();
        }, 50);
      }

      const baseMsg = this.translateService.instant(
        'onlineStore.form.product_listing.messages.validation_error',
      );
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: missingLabels.length ? `${baseMsg}: ${missingLabels.join(', ')}` : baseMsg,
      });
      return;
    }

    if (!this.onlineStoreId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('onlineStore.messages.load_detail_error'),
      });
      return;
    }

    const shopId = parseInt(this.onlineStoreId, 10);

    if (isNaN(shopId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('onlineStore.messages.load_detail_error'),
      });
      return;
    }

    this.loading.set(true);

    // Helper: extract File from image data (only new uploads, not existing API images)
    const extractFileFromImageData = (imageData: any): File | null => {
      if (!imageData) return null;
      if (imageData.isExisting && imageData.previewUrl) return null;
      if (imageData instanceof File) return imageData;
      if (imageData.file && imageData.file instanceof File) {
        if (imageData.file.size === 0 && imageData.isExisting) return null;
        return imageData.file;
      }
      return null;
    };

    const extractImageUrl = (imageData: any): string | null => {
      if (!imageData) return null;
      if (typeof imageData === 'string') return imageData;
      if (imageData.previewUrl && typeof imageData.previewUrl === 'string') return imageData.previewUrl;
      return null;
    };

    // mainImage: single file or single URL for dto
    let mainImageFile: File | undefined;
    let mainImageUrl: string | undefined;
    const mainImage = formValue.mainImage;
    if (mainImage) {
      if (Array.isArray(mainImage)) {
        const first = mainImage[0];
        const file = extractFileFromImageData(first);
        if (file) mainImageFile = file;
        else { const url = extractImageUrl(first); if (url) mainImageUrl = url; }
      } else {
        const file = extractFileFromImageData(mainImage);
        if (file) mainImageFile = file;
        else { const url = extractImageUrl(mainImage); if (url) mainImageUrl = url; }
      }
    }

    // detailImages: list of files and list of URLs for dto
    const detailImageFiles: File[] = [];
    const detailImageUrls: string[] = [];
    const detailImages = formValue.detailImages;
    if (detailImages) {
      const list = Array.isArray(detailImages) ? detailImages : [detailImages];
      list.forEach((item: any) => {
        const file = extractFileFromImageData(item);
        if (file) detailImageFiles.push(file);
        else { const url = extractImageUrl(item); if (url) detailImageUrls.push(url); }
      });
    }

    // Build attributes object from category attributes
    // Key: attribute name, Value: enName (from selected option or user input)
    const attributes: { [key: string]: string } = {};
    const formFields = this.formFields();
    
    formFields.forEach((field) => {
      if (field.controlName.startsWith('categoryAttribute_') && field.customData) {
        const attributeName = field.customData.attributeName;
        const optionMap = field.customData.optionMap as Map<string, string> | undefined;
        const allowFreeText = field.customData.allowFreeText;
        const formValueForKey = formValue[field.controlName];
        
        if (attributeName && formValueForKey !== null && formValueForKey !== undefined && formValueForKey !== '') {
          let finalValue = '';
          
          if (Array.isArray(formValueForKey)) {
            // Multi-select: map each value to enName and join with comma
            const mappedValues = formValueForKey.map((val: string) => {
              if (optionMap && optionMap.has(val)) {
                return optionMap.get(val) || val;
              }
              return val; // Use as-is if not in optionMap (free text)
            });
            finalValue = mappedValues.join(',');
          } else {
            // Single value: map to enName if in optionMap, otherwise use as-is (free text)
            const stringValue = formValueForKey.toString();
            if (optionMap && optionMap.has(stringValue)) {
              finalValue = optionMap.get(stringValue) || stringValue;
            } else {
              finalValue = stringValue; // Use as-is for free text input
            }
          }
          
          if (finalValue) {
            attributes[attributeName] = finalValue;
          }
        }
      }
    });

    // Get actual skuCode from options (formValue.skuCode might be id, not skuCode)
    // Get actual skuCode (prefer detail.skuCode from selectedSkuDetail, fallback to formValue.skuCode)
    let actualSkuCode = '';
    const skuDetail = this.selectedSkuDetail();
    if (skuDetail) {
      actualSkuCode = skuDetail.skuCode || '';
    } else {
      actualSkuCode = formValue.skuCode || '';
    }

    // Per-variant payload: skuCode + edited price + variant's catalog image. Backend maps these to
    // Shopee model.original_price and tier_variation option image_id so variants don't share price/image.
    const selectedVariationSkus: string[] = [];
    const variations: Array<{
      skuCode: string;
      price?: number;
      image?: string;
      addedQuantity?: number;
    }> = [];
    if (this.isShopeeChannel()) {
      const siblingSkusList = this.siblingSkus();
      if (siblingSkusList && siblingSkusList.length > 1) {
        const selected = this.selectedVariationsTable();
        selected.forEach((s) => {
          if (s.skuCode) {
            selectedVariationSkus.push(s.skuCode);
            variations.push({
              skuCode: s.skuCode,
              price:
                s.salesPrice !== null && s.salesPrice !== undefined && s.salesPrice !== ''
                  ? Number(s.salesPrice)
                  : undefined,
              image: Array.isArray(s.materialMainImages) ? s.materialMainImages[0] : undefined,
              addedQuantity:
                s.addedQuantity !== null && s.addedQuantity !== undefined && s.addedQuantity !== ''
                  ? Number(s.addedQuantity)
                  : undefined,
            });
          }
        });
      }
    }

    // Each selected variant must have a price > 0 (Shopee rejects model price <= 0). Block early
    // with a clear message instead of letting the publish fail mid-flight on Shopee.
    if (this.isShopeeChannel() && !isSaveDraft) {
      const invalidVariant = variations.find(
        (v) => v.price === undefined || Number.isNaN(v.price) || v.price <= 0,
      );
      if (invalidVariant) {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'onlineStore.form.product_listing.messages.variant_price_required',
            { sku: invalidVariant.skuCode },
          ),
        });
        return;
      }

      const belowRetail = variations.find((v) => {
        const sibling = this.siblingSkus().find((s) => s.skuCode === v.skuCode);
        const floor = Number(sibling?.originalSalesPrice ?? 0);
        return floor > 0 && (v.price ?? 0) < floor;
      });
      if (belowRetail) {
        const sibling = this.siblingSkus().find((s) => s.skuCode === belowRetail.skuCode);
        const floor = Number(sibling?.originalSalesPrice ?? 0);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'onlineStore.form.product_listing.messages.variant_marketplace_below_retail',
            { sku: belowRetail.skuCode, price: formatCurrencyVND(floor) },
          ),
        });
        return;
      }
    }

    // Single-SKU (Lazada / Shopee): marketplace price must be >= published retail.
    if (
      !isSaveDraft &&
      (this.isLazadaChannel() || this.isShopeeChannel()) &&
      variations.length === 0
    ) {
      const retailFloor = Number(
        formValue.sellingPrice ?? this.formData().sellingPrice ?? 0,
      );
      const marketplacePrice = Number(formValue.marketplacePrice || 0);
      if (retailFloor > 0 && marketplacePrice < retailFloor) {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'onlineStore.form.product_listing.messages.marketplace_below_retail',
            { price: formatCurrencyVND(retailFloor) },
          ),
        });
        return;
      }
    }

    // Build dto (ProductRequestDTO): mainImage/detailImages are existing image URLs; new files sent as mainImage/detailImages parts
    const dto = {
      skuCode: actualSkuCode,
      name: formValue.marketplaceProductName || '',
      description: formValue.productDescription || '',
      price: (() => {
        let p = formValue.marketplacePrice || 0;
        if (!p && variations.length > 0) {
          const valid = variations.filter(v => v.price !== undefined && v.price > 0);
          if (valid.length > 0) p = valid[0].price || 0;
        }
        return p;
      })(),
      categoryId: formValue.marketplaceCategory
        ? parseInt(formValue.marketplaceCategory, 10)
        : undefined,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      mainImage: mainImageUrl,
      detailImages: detailImageUrls.length > 0 ? detailImageUrls : undefined,
      currentQuantity: this.currentLazadaQuantity || 0,
      addedQuantity: !Number.isNaN(additionalQuantity) ? additionalQuantity : 0,
      selectedVariationSkus: selectedVariationSkus.length > 0 ? selectedVariationSkus : undefined,
      variations: variations.length > 0 ? variations : undefined,
    };

    // API: POST /v1/channel-integration/product/upload?shopId=&isSaveDraft= with multipart: mainImage, detailImages, dto
    const basePath = this.onlineShopService.configuration.basePath || '';
    const url = `${basePath}/v1/channel-integration/product/upload`;
    const queryParams = new HttpParams()
      .set('shopId', shopId.toString())
      .set('isSaveDraft', isSaveDraft.toString());
    const formData = new FormData();
    if (mainImageFile) {
      formData.append('mainImage', mainImageFile, mainImageFile.name);
    }
    detailImageFiles.forEach((file) => {
      formData.append('detailImages', file, file.name);
    });
    const dtoBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
    formData.append('dto', dtoBlob);

    this.httpClient
      .post<unknown>(url, formData, { params: queryParams, observe: 'response' })
      .pipe(
        switchMap((response: HttpResponse<any>) => {
          // Check if response is successful (status 200-299)
          if (response.status >= 200 && response.status < 300) {
            // Handle Blob response
            if (response.body instanceof Blob) {
              return from(BlobConverterUtil.convertToJson(response.body)).pipe(
                map((data: any) => {
                  // Ensure response has success property
                  if (data && typeof data === 'object' && !Array.isArray(data)) {
                    // If data already has success property, use it; otherwise set to true
                    return { ...data, success: data.success !== false ? true : false };
                  }
                  // If data is not an object or is array, wrap it
                  return { success: true, data: data };
                })
              );
            }
            // Non-Blob response - ensure it has success property
            const body = response.body || {};
            if (body && typeof body === 'object' && !Array.isArray(body)) {
              return of({ ...body, success: body.success !== false ? true : false });
            }
            return of({ success: true, data: body });
          } else {
            // Non-success status code
            return of({ success: false, error: 'Request failed' });
          }
        }),
        catchError((error) => {
          console.error('Error saving product:', error);
          // Try to extract error message from error response (ensure string, never pass object)
          const raw = error?.error?.message || 
                     error?.error?.msg ||
                     error?.error?.errorMessage ||
                     error?.error ||
                     error?.message || 
                     'Unknown error';
          const errorMessage = typeof raw === 'string' ? raw : (raw?.message ?? raw?.msg ?? JSON.stringify(raw));
          return of({ success: false, error: errorMessage });
        })
      )
      .subscribe({
        next: (response: any) => {
          this.loading.set(false);
          
          // Normalize: response may be body with { error, text } (API 200: {"error":{},"text":"Upload product successfully"})
          let dataForMessage: any = response?.body != null ? response.body : response;
          if (typeof dataForMessage === 'string') {
            try {
              dataForMessage = JSON.parse(dataForMessage);
            } catch {
              dataForMessage = { text: dataForMessage };
            }
          } else if (dataForMessage?.data != null && typeof dataForMessage.data === 'string') {
            try {
              dataForMessage = JSON.parse(dataForMessage.data);
            } catch {
              dataForMessage = { text: dataForMessage.data };
            }
          }
          const body = dataForMessage;
          const err = body?.error ?? response?.error;
          const isEmptyError = err == null ||
            (typeof err === 'string' && (err as string).trim() === '') ||
            (typeof err === 'string' && (err as string).trim() === '{}') ||
            (typeof err === 'object' && !Array.isArray(err) && Object.keys(err).length === 0);
          const hasSuccessText = (body?.text ?? response?.text) && String(body?.text ?? response?.text).toLowerCase().includes('success');
          // Success when: success !== false and (empty error or payload has success text with empty error)
          let isSuccess = response != null &&
            (body?.success ?? response?.success) !== false &&
            isEmptyError &&
            ((body?.success ?? response?.success) === true || (body?.success ?? response?.success) === undefined);
          if (!isSuccess && hasSuccessText && isEmptyError) {
            isSuccess = true; // API 200 with {"error":{},"text":"Upload product successfully"}
          }
          // When catchError passed stringified body as response.error, parse and treat 200 success as success
          if (!isSuccess && typeof response?.error === 'string' && response.error.includes('Upload product successfully')) {
            try {
              const parsed = JSON.parse(response.error);
              if (parsed?.text && String(parsed.text).toLowerCase().includes('success') &&
                  (!parsed?.error || Object.keys(parsed.error || {}).length === 0)) {
                isSuccess = true;
                dataForMessage = parsed;
              }
            } catch {
              // ignore parse error
            }
          }
          
          if (isSuccess) {
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            // Success case - get message from API (support message, msg, text from body or response)
            const apiMessage = dataForMessage?.message ?? dataForMessage?.msg ?? dataForMessage?.text ??
                              response?.message ?? response?.msg ?? response?.text ??
                              response?.data?.message ?? response?.data?.msg ?? response?.data?.text ?? null;
            const successMessageRaw = apiMessage || 
                                  (isSaveDraft
                                    ? this.translateService.instant('onlineStore.form.product_listing.messages.save_draft_success')
                                    : this.translateService.instant('onlineStore.form.product_listing.messages.save_and_publish_success'));
            const successMessage = typeof successMessageRaw === 'string' ? successMessageRaw : (successMessageRaw?.message ?? successMessageRaw?.msg ?? '');
            
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: successMessage,
            });

            // Redirect to online store detail after successful save
            setTimeout(() => {
              this.router.navigate([this.getListRoute()]);
            }, 1000);
          } else {
            // Error case - extract message and ensure detail is always a string (never [object Object])
            const apiErrorMessage = response?.error || 
                                   response?.message || 
                                   response?.msg ||
                                   response?.errorMessage ||
                                   response?.data?.error ||
                                   response?.data?.message ||
                                   response?.data?.msg ||
                                   null;
            const fallback = this.translateService.instant('onlineStore.form.product_listing.messages.save_error');
            const errorMessageRaw = apiErrorMessage ?? fallback;
            const errorMessage = typeof errorMessageRaw === 'string'
              ? errorMessageRaw
              : (errorMessageRaw?.message ?? errorMessageRaw?.msg ?? errorMessageRaw?.errorMessage ?? JSON.stringify(errorMessageRaw));
            
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: errorMessage,
            });
          }
        },
        error: (error) => {
          console.error('Error in save product subscription:', error);
          this.loading.set(false);
          
          // Extract error message and ensure detail is always a string (never [object Object])
          const raw = error?.error?.message || 
                     error?.error?.msg ||
                     error?.error?.errorMessage ||
                     error?.error ||
                     error?.message || 
                     null;
          const errorMessage = typeof raw === 'string'
            ? raw
            : (raw?.message ?? raw?.msg ?? JSON.stringify(raw));
          const detail = errorMessage || 
                        this.translateService.instant('onlineStore.form.product_listing.messages.save_error') || 
                        'Failed to save product';
          
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: typeof detail === 'string' ? detail : String(detail),
          });
        },
      });
  }

  /**
   * Get route to redirect after cancel/back or successful save.
   * If we came from "Quản lý Sản phẩm sàn thương mại", return to /third/goods; otherwise return to online store detail.
   */
  private getListRoute(): string {
    const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    if (state?.returnToMarketplaceProducts) {
      return '/third/goods';
    }
    if (this.onlineStoreId) {
      return `/channel/online-store/view/${this.onlineStoreId}`;
    }
    return '/channel/online-store';
  }

  /**
   * Extract file name from URL
   */
  private extractFileNameFromUrl(url: string): string {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop() || '';
      return fileName || 'image.jpg';
    } catch {
      // If URL parsing fails, try to extract from string
      const parts = url.split('/');
      const fileName = parts[parts.length - 1] || '';
      return fileName.split('?')[0] || 'image.jpg'; // Remove query params
    }
  }

  /**
   * Format currency value
   */
  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || value === 0) {
      return this.translateService.instant('sku_management.detail.values.zero');
    }
    return formatCurrencyVND(value);
  }

  tableSections = computed<TableSection[]>(() => {
    if (!this.isShopeeChannel()) {
      return [];
    }

    const siblingSkusList = this.siblingSkus();
    if (!siblingSkusList || siblingSkusList.length <= 1) {
      return [];
    }

    return [
      {
        title: 'onlineStore.form.product_listing.sections.shopee_variations',
        name: 'shopee_variations',
        show: true,
        insertAfterSection: 'onlineStore.form.product_listing.sections.marketplace_info',
        hasCheckbox: !this.viewMode(),
        hasSerialColumn: false,
        columns: [
          {
            label: 'onlineStore.form.product_listing.fields.sku_code',
            prop: 'skuCode',
            width: 150,
            align: 'left',
          },
          {
            label: 'onlineStore.form.product_listing.fields.sku_name',
            prop: 'skuName',
            width: 200,
            align: 'left',
          },
          {
            label: 'onlineStore.form.product_listing.fields.cost_price',
            prop: 'costPrice',
            type: 'currency',
            width: 120,
            align: 'right',
          },
          {
            label: 'onlineStore.form.product_listing.fields.selling_price',
            prop: 'originalSalesPrice',
            type: 'currency',
            width: 120,
            align: 'right',
          },
          {
            // Editable per-variant price → Shopee model.original_price. Defaults to catalog salesPrice.
            label: 'onlineStore.form.product_listing.fields.marketplace_price',
            prop: 'salesPrice',
            type: this.viewMode() ? 'currency' : 'inputNumber',
            width: 150,
            align: 'right',
            min: 1,
            useGrouping: true,
          },
          {
            // Editable per-variant added quantity
            label: this.viewMode()
              ? 'third_party.goods.table.columns.platform_quantity'
              : 'onlineStore.form.product_listing.fields.additional_quantity',
            prop: 'addedQuantity',
            type: this.viewMode() ? undefined : 'inputNumber',
            width: 150,
            align: 'right',
            min: 0,
          },
        ],
        data: siblingSkusList,
        selection: this.selectedVariationsTable(),
      }
    ];
  });

  onTableAction(event: any): void {
    if (event && (event.section === 'onlineStore.form.product_listing.sections.shopee_variations' || event.section === 'shopee_variations')) {
      if (event.action === 'selectionChange') {
        this.selectedVariationsTable.set(event.data || []);
        this.cdr.markForCheck();
      } else if (event.action === 'cellValueChange' || event.action === 'cellInputChange') {
        const cellData = event.data;
        if (cellData && cellData.rowData) {
          const targetSku = cellData.rowData.skuCode;
          const newValue = cellData.value;
          const prop = cellData.prop;
          
          if (prop === 'salesPrice' || prop === 'addedQuantity') {
            const siblingItem = this.siblingSkus().find(item => item.skuCode === targetSku);
            if (siblingItem) {
              (siblingItem as any)[prop] = newValue;
            }

            const selectedItem = this.selectedVariationsTable().find(item => item.skuCode === targetSku);
            if (selectedItem) {
              (selectedItem as any)[prop] = newValue;
            }
            
            this.cdr.markForCheck();
          }
        }
      }
    }
  }

  /** Load published COST + RETAIL from price-service into readonly form fields. */
  private applyCatalogPricesFromPriceService(
    skuCode: string,
    options?: { preserveMarketplacePrice?: boolean },
  ): void {
    const code = skuCode?.trim();
    if (!code || (!this.isLazadaChannel() && !this.isShopeeChannel())) {
      return;
    }
    forkJoin({
      cost: this.priceListService
        .publishedAmount('COST_DEFAULT', 'COST', code)
        .pipe(catchError(() => of(null))),
      retail: this.priceListService
        .publishedAmount('RETAIL_DEFAULT', 'BASE', code)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ cost, retail }) => {
        const costPrice = cost ?? 0;
        const sellingPrice = retail ?? 0;
        this.formData.update((current) => {
          const next = {
            ...current,
            costPrice,
            sellingPrice,
          };
          if (
            !options?.preserveMarketplacePrice &&
            (current.marketplacePrice == null ||
              current.marketplacePrice === '' ||
              Number(current.marketplacePrice) === 0)
          ) {
            next.marketplacePrice = sellingPrice;
          }
          return next;
        });
        const form = this.addFormComponent?.form;
        if (form) {
          form.get('costPrice')?.setValue(costPrice, { emitEvent: false });
          form.get('sellingPrice')?.setValue(sellingPrice, { emitEvent: false });
          if (
            !options?.preserveMarketplacePrice &&
            (form.get('marketplacePrice')?.value == null ||
              Number(form.get('marketplacePrice')?.value) === 0)
          ) {
            form.get('marketplacePrice')?.setValue(sellingPrice, { emitEvent: false });
          }
        }
        this.cdr.markForCheck();
      });
  }

  /** Enrich Shopee variation rows with cost + retail from price-service. */
  private enrichSiblingPricesFromPriceService(items: any[]): void {
    if (!items.length) {
      this.siblingSkus.set([]);
      this.selectedVariationsTable.set([]);
      this.cdr.markForCheck();
      return;
    }
    forkJoin(
      items.map((item) =>
        forkJoin({
          cost: this.priceListService
            .publishedAmount('COST_DEFAULT', 'COST', item.skuCode)
            .pipe(catchError(() => of(null))),
          retail: this.priceListService
            .publishedAmount('RETAIL_DEFAULT', 'BASE', item.skuCode)
            .pipe(catchError(() => of(null))),
        }).pipe(
          map(({ cost, retail }) => {
            const retailAmount = retail ?? 0;
            const listingPrice = item.salesPrice;
            const hasListingPrice =
              listingPrice !== undefined && listingPrice !== null && listingPrice !== '';
            return {
              ...item,
              costPrice: cost ?? 0,
              originalSalesPrice: retailAmount,
              salesPrice: hasListingPrice ? listingPrice : retailAmount,
            };
          }),
        ),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (enriched) => {
          this.siblingSkus.set(enriched);
          this.selectedVariationsTable.set(enriched);
          this.cdr.markForCheck();
        },
        error: () => {
          this.siblingSkus.set(items);
          this.selectedVariationsTable.set(items);
          this.cdr.markForCheck();
        },
      });
  }
}


